import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Dialer } from "@/components/Dialer";
import {
  LayoutDashboard, FileSpreadsheet, BookOpen, FlaskConical, ShieldCheck,
  Award, Users, Clock, Building2, Phone, LogOut, ChevronDown, Eye, ShieldAlert, UserCog, Menu, X, GraduationCap, ClipboardList, Wrench, FileCheck2, Receipt, FileClock, Syringe, Truck, ScanLine, Beaker, Package, PhoneCall, Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { id: "nav-dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/" },
  { id: "nav-invoices", label: "Invoice Digitization", icon: FileSpreadsheet, path: "/invoices" },
  { id: "nav-scanned-invoices", label: "Scanned Invoices", icon: ScanLine, path: "/scanned-invoices" },
  { id: "nav-formulas", label: "Master Formulas", icon: Beaker, path: "/formulas" },
  { id: "nav-inventory", label: "Inventory", icon: Package, path: "/inventory" },
  { id: "nav-batchlogs", label: "Batch Log", icon: FlaskConical, path: "/batch-logs" },
  { id: "nav-expenses", label: "Pharmacy Expenses", icon: Receipt, path: "/expenses" },
  { id: "nav-sops", label: "SOP Catalog", icon: BookOpen, path: "/sops" },
  { id: "nav-compounding", label: "Compounding Records", icon: FlaskConical, path: "/compounding-logs" },
  { id: "nav-patient-orders", label: "Patient Dose Report", icon: Syringe, path: "/patient-orders" },
  { id: "nav-ups-shipments", label: "UPS Shipment Verifier", icon: Truck, path: "/ups-shipments" },
  { id: "nav-coa", label: "COA Library", icon: FileCheck2, path: "/coa-library" },
  { id: "nav-workorders", label: "Work Orders", icon: Wrench, path: "/work-orders" },
  { id: "nav-compliance", label: "Compliance & Validation", icon: ShieldCheck, path: "/compliance" },
  { id: "nav-training", label: "Training Matrix", icon: GraduationCap, path: "/training-matrix" },
  { id: "nav-licenses", label: "License Tracking", icon: Award, path: "/licenses" },
  { id: "nav-employees", label: "Employee Directory", icon: Users, path: "/employees" },
  { id: "nav-timeclock", label: "Time Clock", icon: Clock, path: "/time-clock" },
  { id: "nav-audit", label: "Audit Log", icon: FileClock, path: "/audit-log" },
  { id: "nav-reports", label: "Inspection Reports", icon: ClipboardList, path: "/reports" },
  { id: "nav-contacts", label: "Phonebook", icon: PhoneCall, path: "/contacts", adminOnly: true },
  { id: "nav-vault", label: "Password Vault", icon: Lock, path: "/vault", adminOnly: true },
  { id: "nav-pharmacy-profile", label: "Pharmacy Profile", icon: Building2, path: "/pharmacy-profile" },
];

const ROLE_LABEL = { admin: "Administrator", pic: "PIC (View-Only)", pharmacy_manager: "Manager (View-Only)", viewer: "View-Only" };

const NavContent = ({ isAdmin, onNavigate }) => (
  <>
    {NAV.filter((it) => !it.adminOnly || isAdmin).map((item) => (
      <NavLink
        key={item.id}
        to={item.path}
        data-testid={`${item.id}-link`}
        end={item.path === "/"}
        onClick={onNavigate}
        className={({ isActive }) =>
          `nucleo-sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
            isActive ? "bg-pink-700 text-white shadow-sm" : "text-[#f7d8c8]/80 hover:bg-[#4a2f22] hover:text-white"
          }`
        }
      >
        <item.icon className="w-[18px] h-[18px]" />
        {item.label}
      </NavLink>
    ))}
    {isAdmin && (
      <NavLink to="/users" data-testid="nav-users-link" onClick={onNavigate}
        className={({ isActive }) =>
          `nucleo-sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
            isActive ? "bg-pink-700 text-white" : "text-[#f7d8c8]/80 hover:bg-[#4a2f22] hover:text-white"
          }`}>
        <UserCog className="w-[18px] h-[18px]" /> User Management
      </NavLink>
    )}
  </>
);

const Brand = () => (
  <div className="h-16 flex items-center gap-2.5 px-6 border-b border-[#4a2f22]">
    <img src="/logo.png" alt="Vera" className="w-9 h-9 rounded-lg object-contain bg-[#faf6f0] p-0.5" />
    <div>
      <div className="font-heading font-bold text-white text-lg leading-none">Vera</div>
      <div className="text-[10px] text-[#f7d8c8]/80 tracking-[0.15em] mt-0.5">PHARMACY OPS</div>
    </div>
  </div>
);

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [dialerOpen, setDialerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const initials = (user?.name || user?.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen bg-[#faf6f0]">
      {/* Desktop sidebar */}
      <aside className="w-64 bg-[#3b2418] text-[#f7d8c8] flex-col hidden md:flex fixed h-screen z-20">
        <Brand />
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          <NavContent isAdmin={isAdmin} />
        </nav>
        <div className="px-4 py-3 border-t border-[#4a2f22] text-[11px] text-[#f7d8c8]/60">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Cleanroom systems nominal
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-[#3b2418]/70" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-[#3b2418] text-[#f7d8c8] flex flex-col shadow-xl animate-fade-up">
            <div className="flex items-center justify-between pr-3">
              <Brand />
              <button data-testid="mobile-drawer-close" onClick={() => setDrawerOpen(false)} className="text-[#f7d8c8]/70 hover:text-white p-2"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
              <NavContent isAdmin={isAdmin} onNavigate={() => setDrawerOpen(false)} />
            </nav>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 md:ml-64 flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-slate-200 shadow-sm flex items-center justify-between px-4 md:px-8 sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <button data-testid="mobile-nav-toggle" onClick={() => setDrawerOpen(true)} className="md:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100">
              <Menu className="w-5 h-5 text-slate-700" />
            </button>
            <div className="font-heading font-semibold text-slate-800 md:hidden">Vera</div>
            <div className="hidden md:block text-sm text-slate-500 font-mono">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <Button data-testid="intermedia-dialer-button" onClick={() => setDialerOpen(true)}
              className="bg-pink-700 hover:bg-pink-800 text-white gap-2 h-9">
              <Phone className="w-4 h-4" /> <span className="hidden sm:inline">Call</span>
            </Button>
            <div className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isAdmin ? "bg-pink-50 text-pink-800" : "bg-amber-50 text-amber-700"}`}>
              {isAdmin ? <ShieldAlert className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              <span data-testid="role-badge">{ROLE_LABEL[user?.role] || "View-Only"}</span>
            </div>
            <div className="relative">
              <button data-testid="profile-menu-button" onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-100">
                <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-xs font-semibold">{initials}</div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-30">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <div className="text-sm font-semibold text-slate-800">{user?.name || "User"}</div>
                    <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    <div className="text-[11px] text-pink-700 mt-1 sm:hidden">{ROLE_LABEL[user?.role]}</div>
                  </div>
                  <button data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }}
                    className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 min-w-0 p-4 md:p-8 max-w-[1500px] w-full">{children}</main>
      </div>

      <Dialer open={dialerOpen} onClose={() => setDialerOpen(false)} />
    </div>
  );
};
